import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { AppBar, Box, Button, Divider, IconButton, TextField, Toolbar, Typography, useMediaQuery, type Theme } from '@mui/material';
import { useFormik } from 'formik';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { type Config } from '../../../../shared/types';
import { drawerStateAtom } from '../../../atoms';
import { useCreateAdminConfig, useDeleteAdminConfig, useUpdateAdminConfig } from '../../../hooks/useAdminConfigs';
import { useDeleteDialog } from '../../../hooks/useDeleteDialog';
import { useSimpleAlert } from '../../../hooks/useSimpleAlert';
import { closedMixinToolbar, openedMixinToolbar } from '../../helpers';
import { useNavigate } from 'react-router';

type Props = {
    config?: Config | null;
};

type ConfigFormValues = {
    _id?: string;
    name: string;
    configJson: string;
};

const initialValues: ConfigFormValues = {
    name: '',
    configJson: '[]',
};

const validationSchema = yup.object().shape({
    name: yup.string().required('Required'),
    configJson: yup
        .string()
        .required('Required')
        .test('valid-json', 'Must be valid JSON', (value) => {
            try {
                JSON.parse(value || '');
                return true;
            } catch {
                return false;
            }
        }),
});

const configToFormValues = (config: Config | null): ConfigFormValues => {
    if (!config) return initialValues;

    return {
        _id: config._id.toString(),
        name: config.name,
        configJson: JSON.stringify(config.config, null, 2),
    };
};

export const ConfigForm = ({ config = null }: Props) => {
    const navigate = useNavigate();
    const [drawerState] = useAtom(drawerStateAtom);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { mutate: updateAdminConfig } = useUpdateAdminConfig();
    const { mutate: createAdminConfig } = useCreateAdminConfig();
    const { mutate: deleteAdminConfig } = useDeleteAdminConfig();
    const { DeleteDialog, showDialog: showDeleteDialog } = useDeleteDialog();
    const { showSimpleAlert } = useSimpleAlert();

    const onBack = () => {
        navigate('/admin/configs');
    };

    const onSubmit = async (values: ConfigFormValues) => {
        const { _id, name, configJson } = values;
        const parsedConfig = JSON.parse(configJson);

        if (_id) {
            updateAdminConfig(
                { id: _id, data: { name, config: parsedConfig } },
                {
                    onSuccess: (data) => {
                        showSimpleAlert({ title: 'Config successfully updated' });
                        formik.resetForm({ values: configToFormValues(data) });
                    },
                },
            );
        } else {
            createAdminConfig(
                { name, config: parsedConfig },
                {
                    onSuccess: () => {
                        showSimpleAlert({ title: 'Config successfully created' });
                        formik.resetForm({ values: initialValues });
                    },
                },
            );
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleShowDeleteDialog = () => {
        showDeleteDialog({ title: `Confirm DELETE of config ${config?.name}`, onDelete: handleDeleteConfig });
    };

    const handleDeleteConfig = () => {
        if (config) {
            deleteAdminConfig(config._id.toString(), {
                onSuccess: () => {
                    showSimpleAlert({ title: 'Config successfully deleted', severity: 'info' });
                    onBack();
                },
                onError: () => {
                    showSimpleAlert({ title: 'Unable to delete the config', severity: 'error' });
                },
            });
        }
    };

    useEffect(() => {
        formik.resetForm({ values: configToFormValues(config) });
    }, [config]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ maxWidth: (theme) => theme.breakpoints.values.md, p: 3, pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2 }}>
                        <Button variant="outlined" size="large" sx={{ minWidth: 0, pl: 1, pr: 1 }} onClick={onBack} startIcon={<NavigateBeforeIcon />}>
                            Configs
                        </Button>
                        {config && (
                            <Typography variant="h6" gutterBottom={false}>
                                {config.name}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
                        Config Details
                    </Typography>
                    <Divider />
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'scroll',
                    }}>
                    <form noValidate autoComplete="off">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                maxWidth: (theme) => theme.breakpoints.values.md,
                                p: 3,
                                mb: 8,
                            }}>
                            <TextField
                                aria-describedby="name-text"
                                label="Name"
                                placeholder="Name"
                                type="text"
                                name="name"
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                            />
                            <TextField
                                aria-describedby="configJson-text"
                                label="Config (JSON)"
                                placeholder="{}"
                                type="text"
                                name="configJson"
                                multiline
                                minRows={10}
                                maxRows={30}
                                value={formik.values.configJson}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.touched.configJson && formik.errors.configJson)}
                                helperText={formik.touched.configJson && formik.errors.configJson}
                                InputProps={{
                                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                                }}
                            />
                        </Box>
                    </form>
                </Box>
            </Box>
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Toolbar
                    sx={(theme) => ({
                        ...(isNotSm &&
                            drawerState && {
                                ...openedMixinToolbar(theme),
                            }),
                        ...(isNotSm &&
                            !drawerState && {
                                ...closedMixinToolbar(theme),
                            }),
                        maxWidth: (theme) => theme.breakpoints.values.md,
                    })}>
                    {config && (
                        <IconButton aria-label="delete" size="large" onClick={handleShowDeleteDialog}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <DeleteDialog />
        </Box>
    );
};
